/**
 * What every chart kind is built from: scales with clean numbers, a legend, a smooth line, the
 * timing of an entrance, and the marks a finger or a pointer can find.
 *
 * Kept apart from the kinds themselves (`./charts.ts`) so each kind reads as the decisions that
 * make it that kind — where its bars go, what it labels — and not as the plumbing they share.
 */
import { formatChartValue } from '@orbit-mind/domain';
import { baseline, fitText, line, move, textWidth, } from './primitives';
/** Label text. Values and names are ink — never the series colour; the mark beside them is. */
export const FONT = 9;
export const SMALL_FONT = 8;
/** Secondary text, tick labels, grid lines and the baseline: each a step quieter than the last. */
export const MUTED = 0.66;
export const FAINT = 0.48;
export const GRID = 0.12;
export const AXIS = 0.32;
export const LEGEND_HEIGHT = 14;
export function portuguese(context) {
    return context.format?.decimal === ',';
}
export function clamp(value, low, high) {
    return Math.min(high, Math.max(low, value));
}
export function lerp(from, to, t) {
    return from + (to - from) * t;
}
/** Fast at first, settling gently: what a mark growing into place should feel like. */
export function easeOutCubic(t) {
    const clamped = clamp(t, 0, 1);
    return 1 - (1 - clamped) ** 3;
}
/**
 * Progress for the `index`-th of `count` marks: each starts a little after the one before, so a
 * row of columns rises as a wave rather than as one block. Exactly 1 whenever `t` is.
 */
export function staged(t, index, count, spread = 0.35) {
    if (t >= 1)
        return 1;
    if (count <= 1)
        return easeOutCubic(t);
    const delay = (spread * index) / (count - 1);
    return easeOutCubic((t - delay) / (1 - spread));
}
/**
 * A number part-way through counting up to its final value, with no more decimals than the final
 * value has: a gauge heading for 72% passes 60%, never 60,02%.
 */
export function countUp(value, grow) {
    if (grow >= 1)
        return value;
    const decimals = Number.isInteger(value)
        ? 0
        : Math.min(2, (String(value).split('.')[1] ?? '').length);
    const factor = 10 ** decimals;
    return Math.round(value * grow * factor) / factor;
}
/** A number as the chart writes it, with the chart's unit and the reader's decimal comma. */
export function formatValue(frame, value, withUnit = true) {
    const unit = frame.content.unit;
    return formatChartValue(value, {
        ...(withUnit && unit !== undefined ? { unit } : {}),
        ...(frame.context.format === undefined ? {} : { format: frame.context.format }),
    });
}
/** A share of a whole as a whole percentage; "<1%" rather than a misleading "0%". */
export function formatShare(frame, share) {
    if (!Number.isFinite(share) || share <= 0)
        return '0%';
    const percent = share * 100;
    if (percent < 1)
        return '<1%';
    const rounded = percent < 10 ? Math.round(percent * 10) / 10 : Math.round(percent);
    const text = String(rounded);
    return `${portuguese(frame.context) ? text.replace('.', ',') : text}%`;
}
/**
 * An axis that ends on a round number and steps in 1s, 2s, 2.5s or 5s — the numbers a person
 * would write on a whiteboard, never "0, 23.3, 46.7".
 */
export function niceScale(low, high, count = 3, quarters = true) {
    let min = Math.min(low, high);
    let max = Math.max(low, high);
    if (max === min)
        max = min === 0 ? 1 : min + Math.abs(min);
    if (max === min)
        max = min + 1;
    const raw = (max - min) / Math.max(1, count);
    const magnitude = 10 ** Math.floor(Math.log10(raw));
    const normal = raw / magnitude;
    const rounded = normal <= 1 ? 1 : normal <= 2 ? 2 : quarters && normal <= 2.5 ? 2.5 : normal <= 5 ? 5 : 10;
    // A step below one on a counted axis would be half a week: count in ones instead.
    const step = !quarters && rounded * magnitude < 1 ? 1 : rounded * magnitude;
    min = Math.floor(min / step + 1e-9) * step;
    max = Math.ceil(max / step - 1e-9) * step;
    const ticks = [];
    for (let value = min; value <= max + step / 2; value += step) {
        ticks.push(Math.round(value / step) * step);
    }
    return { min, max, ticks };
}
export function textOf(frame, x, y, text, options = {}) {
    if (text === '')
        return;
    const opacity = options.opacity ?? 1;
    if (opacity <= 0.01)
        return;
    frame.primitives.push({
        kind: 'text',
        x,
        y,
        text,
        size: options.size ?? FONT,
        weight: options.weight ?? 400,
        anchor: options.anchor ?? 'start',
        fill: options.fill ?? 'ink',
        ...(opacity >= 1 ? {} : { opacity }),
    });
}
export function widthOf(frame, text, size = FONT, weight = 400) {
    return textWidth(text, size, weight, frame.context);
}
export function fit(frame, text, width, size = FONT, weight = 400) {
    return width <= 4 ? '' : fitText(text, width, size, weight, frame.context);
}
/** A name for a series nobody named: "Série 2". */
export function seriesName(frame, name, index) {
    if (name.trim() !== '')
        return name;
    return `${portuguese(frame.context) ? 'Série' : 'Series'} ${String(index + 1)}`;
}
/**
 * A row of swatches and names. Names are cut to share the width evenly when they do not all fit;
 * a legend that runs off the card is worse than one that abbreviates.
 */
export function legendRow(frame, items, x, y, width) {
    if (items.length === 0)
        return;
    const swatch = 8;
    const gap = 10;
    const natural = items.map((item) => widthOf(frame, item.name, SMALL_FONT));
    const total = natural.reduce((sum, value) => sum + value, 0) +
        items.length * (swatch + 4) +
        (items.length - 1) * gap;
    const share = (width - items.length * (swatch + 4) - (items.length - 1) * gap) / items.length;
    let left = x + Math.max(0, (width - Math.min(total, width)) / 2);
    const middle = y + LEGEND_HEIGHT / 2;
    items.forEach((item, index) => {
        const name = total <= width ? item.name : fit(frame, item.name, Math.max(0, share), SMALL_FONT);
        frame.primitives.push({
            kind: 'rect',
            rect: { x: left, y: middle - swatch / 2, width: swatch, height: swatch },
            radius: 2,
            fill: { series: item.color },
        });
        textOf(frame, left + swatch + 4, baseline(middle, SMALL_FONT), name, {
            size: SMALL_FONT,
            opacity: MUTED,
        });
        left += swatch + 4 + (total <= width ? (natural[index] ?? 0) : Math.max(0, share)) + gap;
    });
}
/**
 * Hairlines at the ticks, and the tick numbers at their left when asked for. One path for all the
 * lines: a grid is one quiet thing, not five.
 */
export function horizontalGrid(frame, plot, scale, yOf, labelled) {
    const segments = [];
    for (const tick of scale.ticks) {
        if (tick === 0)
            continue;
        const y = yOf(tick);
        segments.push(move(plot.x, y), line(plot.x + plot.width, y));
    }
    if (segments.length > 0) {
        frame.primitives.push({
            kind: 'path',
            segments,
            stroke: 'ink',
            strokeWidth: 0.6,
            opacity: GRID,
        });
    }
    if (!labelled)
        return;
    for (const tick of scale.ticks) {
        textOf(frame, plot.x - 3, baseline(yOf(tick), SMALL_FONT), tickText(frame, tick), {
            size: SMALL_FONT,
            anchor: 'end',
            opacity: FAINT,
        });
    }
}
/** Ticks carry a percent sign but not a currency: "R$" on every tick is noise. */
export function tickText(frame, value) {
    return formatValue(frame, value, frame.content.unit === '%');
}
export function tickWidth(frame, scale) {
    return Math.max(...scale.ticks.map((tick) => widthOf(frame, tickText(frame, tick), SMALL_FONT)), 0);
}
/**
 * Labels along a bottom edge, one per band, skipping every other one (or more) when they would
 * run into each other.
 */
export function bottomLabels(frame, labels, centreOf, band, y) {
    const widest = Math.max(...labels.map((label) => widthOf(frame, label, SMALL_FONT)), 0);
    const every = Math.max(1, Math.ceil((Math.min(widest, 30) + 4) / Math.max(1, band)));
    labels.forEach((label, index) => {
        if (index % every !== 0)
            return;
        textOf(frame, centreOf(index), y, fit(frame, label, band * every - 3, SMALL_FONT), {
            size: SMALL_FONT,
            anchor: 'middle',
            opacity: MUTED,
        });
    });
}
/** A dot with a ring of the card's colour around it, so it reads over a line or another dot. */
export function dot(frame, x, y, r, color, ring = 1.5) {
    frame.primitives.push({
        kind: 'circle',
        cx: x,
        cy: y,
        r,
        fill: { series: color },
        stroke: 'surface',
        strokeWidth: ring,
    });
}
/**
 * A smooth line through the points that never overshoots them (monotone cubic, Fritsch–Carlson):
 * a curve that dips below zero between two positive months would be telling a lie.
 */
export function monotoneCurve(points) {
    const n = points.length;
    if (n < 2)
        return [];
    const slopes = [];
    for (let index = 0; index < n - 1; index += 1) {
        const a = points[index] ?? { x: 0, y: 0 };
        const b = points[index + 1] ?? a;
        const dx = b.x - a.x;
        slopes.push(dx === 0 ? 0 : (b.y - a.y) / dx);
    }
    const tangents = points.map((_, index) => {
        if (index === 0)
            return slopes[0] ?? 0;
        if (index === n - 1)
            return slopes[n - 2] ?? 0;
        const before = slopes[index - 1] ?? 0;
        const after = slopes[index] ?? 0;
        return before * after <= 0 ? 0 : (before + after) / 2;
    });
    for (let index = 0; index < n - 1; index += 1) {
        const slope = slopes[index] ?? 0;
        if (slope === 0) {
            tangents[index] = 0;
            tangents[index + 1] = 0;
            continue;
        }
        const a = (tangents[index] ?? 0) / slope;
        const b = (tangents[index + 1] ?? 0) / slope;
        const h = a * a + b * b;
        if (h > 9) {
            const tau = 3 / Math.sqrt(h);
            tangents[index] = tau * a * slope;
            tangents[index + 1] = tau * b * slope;
        }
    }
    const pieces = [];
    for (let index = 0; index < n - 1; index += 1) {
        const from = points[index] ?? { x: 0, y: 0 };
        const to = points[index + 1] ?? from;
        const third = (to.x - from.x) / 3;
        pieces.push({
            from,
            control1: { x: from.x + third, y: from.y + (tangents[index] ?? 0) * third },
            control2: { x: to.x - third, y: to.y - (tangents[index + 1] ?? 0) * third },
            to,
        });
    }
    return pieces;
}
function split(piece, u) {
    const mix = (a, b) => ({ x: lerp(a.x, b.x, u), y: lerp(a.y, b.y, u) });
    const ab = mix(piece.from, piece.control1);
    const bc = mix(piece.control1, piece.control2);
    const cd = mix(piece.control2, piece.to);
    const abc = mix(ab, bc);
    const bcd = mix(bc, cd);
    return { from: piece.from, control1: ab, control2: abc, to: mix(abc, bcd) };
}
/**
 * The curve drawn up to a fraction of its length along x — how a line draws itself in. Control
 * points sit at thirds, so a piece's x is linear in its parameter and cutting at a parameter cuts
 * at that x exactly.
 */
export function curveUpTo(points, fraction) {
    const first = points[0] ?? { x: 0, y: 0 };
    if (points.length < 2)
        return { segments: [move(first.x, first.y)], tip: first };
    const pieces = monotoneCurve(points);
    const reach = clamp(fraction, 0, 1) * pieces.length;
    const segments = [move(first.x, first.y)];
    let tip = first;
    for (let index = 0; index < pieces.length; index += 1) {
        const piece = pieces[index];
        if (piece === undefined || reach <= index)
            break;
        const drawn = reach >= index + 1 ? piece : split(piece, reach - index);
        segments.push({
            kind: 'cubic',
            control1: drawn.control1,
            control2: drawn.control2,
            to: drawn.to,
        });
        tip = drawn.to;
    }
    return { segments, tip };
}
export function rectMark(frame, rect, anchor, title, lines) {
    frame.marks.push({ area: { kind: 'rect', rect }, anchor, title, lines });
}
/** The mark under a point, if any. Later marks win: they were drawn on top. */
export function hitChartMark(marks, point) {
    for (let index = marks.length - 1; index >= 0; index -= 1) {
        const mark = marks[index];
        if (mark === undefined)
            continue;
        const area = mark.area;
        if (area.kind === 'rect') {
            const { x, y, width, height } = area.rect;
            if (point.x >= x && point.x <= x + width && point.y >= y && point.y <= y + height)
                return mark;
            continue;
        }
        const dx = point.x - area.cx;
        const dy = point.y - area.cy;
        const distance = Math.hypot(dx, dy);
        if (distance < area.inner || distance > area.outer)
            continue;
        let angle = Math.atan2(dy, dx);
        while (angle < area.from)
            angle += Math.PI * 2;
        while (angle > area.from + Math.PI * 2)
            angle -= Math.PI * 2;
        if (angle <= area.to)
            return mark;
    }
    return undefined;
}
const TOOLTIP_PAD = 7;
const TOOLTIP_TITLE = 9.5;
const TOOLTIP_LINE = 13;
/**
 * The bubble a tapped mark shows: its label, then a line per series — swatch, name, value. Ink on
 * the card's colour turned inside out, so it stands out on a white card and on a navy one alike.
 */
export function chartTooltip(mark, bounds, context) {
    const measure = (text, size, weight) => textWidth(text, size, weight, context);
    const rows = mark.lines.map((row) => {
        const swatch = row.color === null ? 0 : 12;
        const nameWidth = row.name === '' ? 0 : measure(row.name, FONT, 400) + 8;
        return { row, swatch, nameWidth, valueWidth: measure(row.value, FONT, 700) };
    });
    const titleWidth = mark.title === '' ? 0 : measure(mark.title, TOOLTIP_TITLE, 700);
    const width = Math.min(220, Math.max(titleWidth, ...rows.map((row) => row.swatch + row.nameWidth + row.valueWidth), 24) +
        TOOLTIP_PAD * 2);
    const height = TOOLTIP_PAD * 2 + (mark.title === '' ? 0 : TOOLTIP_LINE) + rows.length * TOOLTIP_LINE - 2;
    const above = mark.anchor.y - bounds.y >= height * 0.6;
    const x = clamp(mark.anchor.x - width / 2, bounds.x - 12, bounds.x + bounds.width + 12 - width);
    const y = above ? mark.anchor.y - height - 7 : mark.anchor.y + 7;
    const tip = clamp(mark.anchor.x, x + 8, x + width - 8);
    const primitives = [
        {
            kind: 'path',
            segments: above
                ? [
                    move(tip - 5, y + height - 0.5),
                    line(tip, y + height + 5),
                    line(tip + 5, y + height - 0.5),
                ]
                : [move(tip - 5, y + 0.5), line(tip, y - 5), line(tip + 5, y + 0.5)],
            fill: 'ink',
            opacity: 0.94,
        },
        { kind: 'rect', rect: { x, y, width, height }, radius: 6, fill: 'ink', opacity: 0.94 },
    ];
    let cursor = y + TOOLTIP_PAD;
    if (mark.title !== '') {
        primitives.push({
            kind: 'text',
            x: x + TOOLTIP_PAD,
            y: baseline(cursor + TOOLTIP_LINE / 2 - 1, TOOLTIP_TITLE),
            text: fitText(mark.title, width - TOOLTIP_PAD * 2, TOOLTIP_TITLE, 700, context),
            size: TOOLTIP_TITLE,
            weight: 700,
            anchor: 'start',
            fill: 'surface',
        });
        cursor += TOOLTIP_LINE;
    }
    for (const { row, swatch } of rows) {
        const middle = cursor + TOOLTIP_LINE / 2 - 1;
        if (row.color !== null) {
            primitives.push({
                kind: 'rect',
                rect: { x: x + TOOLTIP_PAD, y: middle - 4, width: 8, height: 8 },
                radius: 2,
                fill: { series: row.color },
            });
        }
        if (row.name !== '') {
            primitives.push({
                kind: 'text',
                x: x + TOOLTIP_PAD + swatch,
                y: baseline(middle, FONT),
                text: row.name,
                size: FONT,
                weight: 400,
                anchor: 'start',
                fill: 'surface',
                opacity: 0.8,
            });
        }
        primitives.push({
            kind: 'text',
            x: row.name === '' ? x + TOOLTIP_PAD + swatch : x + width - TOOLTIP_PAD,
            y: baseline(middle, FONT),
            text: row.value,
            size: FONT,
            weight: 700,
            anchor: row.name === '' ? 'start' : 'end',
            fill: 'surface',
        });
        cursor += TOOLTIP_LINE;
    }
    return primitives;
}
//# sourceMappingURL=chart-kit.js.map